"use client";

import { ArrowRight, ChevronRight, Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";
import { Logo } from "@/components/logo";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { HeliosGlyph } from "@/modules/marketing/home/member-data-gap-icon";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Button } from "./ui/button";
import {
  type AppPromo,
  type CustomerStory,
  type FeaturedCard,
  MARKETING_NAV,
  type MenuLink,
  type NavEntry,
} from "./marketing-nav/nav-data";

/* -------------------------------------------------------------------------- */
/* Shared pieces                                                              */
/* -------------------------------------------------------------------------- */

function RowIcon({ item }: { item: MenuLink }) {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border">
      {item.iconImage ? (
        <Image
          src={item.iconImage}
          alt=""
          width={32}
          height={32}
          className="size-5 rounded-[5px] object-contain ring-1 ring-black/5 dark:ring-white/10"
        />
      ) : item.monogram ? (
        <span className="text-[11px] font-semibold tracking-tight text-muted-foreground">
          {item.monogram}
        </span>
      ) : item.icon ? (
        <item.icon className="size-[18px] text-muted-foreground" />
      ) : null}
    </span>
  );
}

function hasGlyph(item: MenuLink) {
  return Boolean(item.icon || item.iconImage || item.monogram);
}

function MenuRow({ item }: { item: MenuLink }) {
  return (
    <NavigationMenuLink asChild>
      <Link
        href={item.href}
        className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-muted"
      >
        {hasGlyph(item) ? <RowIcon item={item} /> : null}
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">{item.title}</span>
          {item.description ? (
            <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">
              {item.description}
            </span>
          ) : null}
        </span>
      </Link>
    </NavigationMenuLink>
  );
}

function StoryRail({ story }: { story: CustomerStory }) {
  return (
    <NavigationMenuLink asChild>
      <Link
        href={story.href}
        className="group flex w-64 flex-col justify-between rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted"
      >
        <div>
          <span className="text-xs font-semibold text-muted-foreground">
            {story.eyebrow}
          </span>
          <p className="mt-3 text-[15px] font-medium leading-snug text-foreground text-balance">
            &ldquo;{story.quote}&rdquo;
          </p>
        </div>
        <div className="mt-5">
          <div className="flex items-center gap-2.5">
            <Image
              src={story.image}
              alt={story.org}
              width={40}
              height={40}
              className="size-9 rounded-md object-cover"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-foreground">{story.name}</div>
              <div className="text-[13px] text-muted-foreground">{story.org}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1 text-[13px] font-semibold text-primary dark:text-brand-accent">
            Read customer story
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function FeaturedRail({ featured }: { featured: FeaturedCard }) {
  return (
    <NavigationMenuLink asChild>
      <Link
        href={featured.href}
        className="group flex w-64 flex-col justify-between rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted"
      >
        <div>
          <span className="text-xs font-semibold text-muted-foreground">
            {featured.eyebrow}
          </span>
          <p className="mt-3 text-[15px] font-semibold leading-snug text-foreground text-balance">
            {featured.title}
          </p>
          <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
            {featured.description}
          </p>
        </div>
        <div className="mt-5 flex items-center gap-1 text-[13px] font-semibold text-primary dark:text-brand-accent">
          {featured.cta}
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function AppPromoRail({ promo }: { promo: AppPromo }) {
  return (
    <NavigationMenuLink asChild>
      <a
        href={promo.href}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex w-64 flex-col justify-between rounded-xl bg-muted/50 p-5 transition-colors hover:bg-muted"
      >
        <div>
          {/* iOS-style app icon with an unread badge (homepage treatment). */}
          <div className="mt-2 mb-6 flex justify-center">
            <div className="relative -rotate-6">
              <div className="flex size-16 items-center justify-center rounded-[22%] bg-white shadow-[0_10px_30px_-8px_rgba(17,12,46,0.35)] ring-1 ring-black/5">
                <HeliosGlyph className="size-9" />
              </div>
              <span className="absolute -right-2.5 -top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1.5 text-[12px] font-bold leading-none text-white shadow-sm ring-2 ring-background">
                3
              </span>
            </div>
          </div>
          <span className="text-center block text-xs font-semibold text-muted-foreground">
            {promo.eyebrow}
          </span>
          <p className="mt-2 text-center text-[15px] font-semibold leading-snug text-foreground text-balance">
            {promo.title}
          </p>
        </div>
        <div className="mt-5 flex items-center justify-center gap-1 text-[13px] font-semibold text-primary dark:text-brand-accent">
          {promo.cta}
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </a>
    </NavigationMenuLink>
  );
}

/* -------------------------------------------------------------------------- */
/* Desktop                                                                    */
/* -------------------------------------------------------------------------- */

function DesktopEntry({ entry }: { entry: NavEntry }) {
  if (entry.type === "link") {
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Link
            href={entry.href}
            className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:text-foreground/70"
          >
            {entry.title}
          </Link>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  const hasRail = Boolean(entry.story || entry.featured || entry.appPromo);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="h-9 bg-transparent px-3 text-sm font-medium text-foreground hover:bg-transparent hover:text-foreground/70 focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-foreground/70">
        {entry.title}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="flex p-2.5">
          <ul
            className={cn(
              "grid content-start gap-0.5",
              entry.columns === 2 ? "w-[34rem] grid-cols-2" : "w-[21rem] grid-cols-1",
            )}
          >
            {entry.items.map((item) => (
              <li key={item.title}>
                <MenuRow item={item} />
              </li>
            ))}
          </ul>
          {hasRail ? (
            <div className="-my-2.5 ml-2.5 self-stretch border-l border-border py-2.5 pl-2.5">
              {entry.story ? <StoryRail story={entry.story} /> : null}
              {entry.featured ? <FeaturedRail featured={entry.featured} /> : null}
              {entry.appPromo ? <AppPromoRail promo={entry.appPromo} /> : null}
            </div>
          ) : null}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

/* -------------------------------------------------------------------------- */
/* Mobile                                                                     */
/* -------------------------------------------------------------------------- */

function MobileEntry({ entry }: { entry: NavEntry }) {
  if (entry.type === "link") {
    return (
      <Link
        href={entry.href}
        className="flex items-center justify-between border-b border-border py-4 text-lg font-medium text-foreground"
      >
        {entry.title}
      </Link>
    );
  }

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value={entry.title} className="border-b border-border">
        <AccordionTrigger className="py-4 text-lg font-medium !no-underline">
          {entry.title}
        </AccordionTrigger>
        <AccordionContent className="pb-2">
          <ul className="flex flex-col gap-0.5">
            {entry.items.map((item) => (
              <li key={item.title}>
                <Link href={item.href} className="group flex items-start gap-3 rounded-xl p-2.5">
                  {hasGlyph(item) ? <RowIcon item={item} /> : null}
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-foreground">
                      {item.title}
                    </span>
                    {item.description ? (
                      <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

/* -------------------------------------------------------------------------- */
/* Root                                                                       */
/* -------------------------------------------------------------------------- */

export function MainNav() {
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const pathname = usePathname();

  React.useEffect(() => {
    setShowMobileMenu(false);
  }, [pathname]);

  return (
    <>
      <nav className="mx-auto flex max-w-full items-center justify-between gap-x-4 p-6 transition-all sm:max-w-7xl lg:px-8">
        <div className="flex xl:flex-1">
          <Link href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">{siteConfig.name}</span>
            <Logo className="h-9 w-auto" alt="Helios SMS Marketing" />
          </Link>
        </div>

        <div className="hidden xl:flex">
          <NavigationMenu>
            <NavigationMenuList className="gap-1">
              {MARKETING_NAV.map((entry) => (
                <DesktopEntry entry={entry} key={entry.title} />
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex flex-1 items-center justify-end gap-x-3 sm:gap-x-5">
          <Link
            href="/login"
            className="hidden whitespace-nowrap text-sm leading-6 text-foreground underline-offset-4 hover:underline xl:block"
          >
            Log in
          </Link>
          <Link
            href="/signup"
            className="hidden whitespace-nowrap text-sm leading-6 text-foreground underline-offset-4 hover:underline xl:block"
          >
            Sign up
          </Link>
          <Button size="sm" asChild className="group/button transition-all">
            <Link href="/book-a-demo" className="whitespace-nowrap px-3 py-2">
              Book a demo
              <ArrowRight className="relative ml-2 h-3 w-3 transition-transform group-hover/button:translate-x-0.5" />
            </Link>
          </Button>
          <Button
            size="sm"
            className="w-9 shadow-none xl:hidden"
            variant="ghost"
            aria-label="Toggle menu"
            onClick={() => setShowMobileMenu((open) => !open)}
          >
            {showMobileMenu ? (
              <X className="h-5 w-5 shrink-0" />
            ) : (
              <Menu className="h-5 w-5 shrink-0" />
            )}
          </Button>
        </div>
      </nav>

      <div
        className={cn(
          showMobileMenu ? "flex flex-col" : "hidden",
          "absolute top-[85px] z-10 h-[calc(100vh-85px)] w-full overflow-y-auto border-t border-border bg-background px-6 pb-20 pt-2 xl:hidden",
        )}
      >
        <nav className="flex grow flex-col">
          {MARKETING_NAV.map((entry) => (
            <MobileEntry entry={entry} key={entry.title} />
          ))}
          <Link
            href="/login"
            className="flex items-center justify-between border-b border-border py-4 text-lg font-medium text-foreground"
          >
            Log in
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        </nav>

        <div className="mt-6 flex flex-col gap-3">
          <Button size="xl" asChild className="w-full">
            <Link href="/book-a-demo">Book a demo</Link>
          </Button>
          <Button size="xl" variant="outline" asChild className="w-full">
            <Link href="/signup">Sign up</Link>
          </Button>
        </div>
      </div>
    </>
  );
}
